import {
  ArrowForward,
  KeyOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { Button, Fade, useMediaQuery } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { AuthPageLayout } from "@/components/auth";
import { AuthFormError, AuthInput, AuthSubmitButton } from "@/components/auth/AuthFormControls";
import AuthTransition from "@/components/auth/AuthTransition";
import SignInPanel from "@/components/auth/SignInPanel";
import { useAuth } from "@/contexts/AuthContext";
import { paths } from "@/routes/paths";
import { CoreService, LoginError, SsoProvider } from "@/services/core/CoreService";
import { getCurrentAppPath, getSafeReturnPath } from "@/utils/authRedirect";
import { UserRole } from "@/utils/roleManager";

interface LoginLocationState {
  from?: Location;
}

const authLinkClassName =
  "cursor-pointer disabled:cursor-not-allowed text-sm text-blue-600 hover:text-blue-700 hover:underline focus-visible:underline transition-colors duration-200";

interface SsoLoginBodyProps {
  providers: SsoProvider[];
  selectedProviderId: number;
  isLoading: boolean;
  onProviderChange: (providerId: number) => void;
}

const SsoLoginBody = ({
  providers,
  selectedProviderId,
  isLoading,
  onProviderChange,
}: SsoLoginBodyProps) => {
  const { t } = useTranslation();

  return (
    <select
      aria-label={t("login.actions.useSso")}
      value={selectedProviderId}
      disabled={isLoading}
      onChange={(event) => onProviderChange(Number(event.target.value))}
      className="block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg bg-white/50 text-left rtl:text-right"
    >
      {providers.map((provider) => (
        <option key={provider.id} value={provider.id}>
          {provider.name}
        </option>
      ))}
    </select>
  );
};

interface PasswordLoginBodyProps {
  isActive: boolean;
  password: string;
  isLoading: boolean;
  onPasswordChange: (password: string) => void;
}

const PasswordLoginBody = ({
  isActive,
  password,
  isLoading,
  onPasswordChange,
}: PasswordLoginBodyProps) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div aria-hidden={!isActive} inert={!isActive}>
      <label
        htmlFor="password"
        className="block text-md text-gray-700 mb-2 text-left rtl:text-right"
      >
        {t("login.password")}
      </label>
      <div className="relative">
        <AuthInput
          id="password"
          name="password"
          type={showPassword ? "text" : "password"}
          required={isActive}
          className="pr-12 rtl:pr-4 rtl:pl-12"
          placeholder={t("login.placeholders.password")}
          value={password}
          onChange={(event) => onPasswordChange(event.target.value)}
          autoComplete="current-password"
          disabled={isLoading || !isActive}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
        />
        <button
          type="button"
          disabled={isLoading || !isActive}
          className="absolute inset-y-0 right-0 rtl:right-auto rtl:left-0 pr-4 rtl:pr-0 rtl:pl-4 flex items-center hover:bg-gray-50 rounded-r-xl rtl:rounded-r-none rtl:rounded-l-xl transition-colors duration-200"
          onClick={() => setShowPassword((visible) => !visible)}
          aria-label={t("login.accessibility.togglePasswordVisibility")}
        >
          {showPassword ? (
            <VisibilityOffOutlined sx={{ fontSize: 20, color: "#9ca3af" }} />
          ) : (
            <VisibilityOutlined sx={{ fontSize: 20, color: "#9ca3af" }} />
          )}
        </button>
      </div>
      <div className="flex justify-end mt-2">
        <Link to="/forgot-password" className={authLinkClassName}>
          {t("login.forgotPassword")}
        </Link>
      </div>
    </div>
  );
};

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorState, setErrorState] = useState({ message: "", visible: false });
  const error = errorState.visible ? errorState.message : "";
  const setError = useCallback((message: string) => {
    setErrorState((previous) => ({
      message: message || previous.message,
      visible: Boolean(message),
    }));
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  const [isSsoPending, setIsSsoPending] = useState(false);
  const [ssoRedirectUrl, setSsoRedirectUrl] = useState<string | null>(null);
  const [isSsoLoadingVisible, setIsSsoLoadingVisible] = useState(false);
  const [providers, setProviders] = useState<SsoProvider[] | null>(null);
  const [usePassword, setUsePassword] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const transitionDuration = prefersReducedMotion ? 0 : 220;
  const hasProviders = providers !== null && providers.length > 0;
  const showPasswordForm = providers !== null && (!hasProviders || usePassword);
  const showSso = hasProviders && !usePassword;
  const selectedProvider =
    providers?.find((provider) => provider.id === selectedProviderId) ?? providers?.[0];

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("expired") === "true") {
      setError(t("login.errors.sessionHasExpiredLog"));
    }
  }, [location.search, setError, t]);

  useEffect(() => {
    if (!user) return;

    const searchParams = new URLSearchParams(location.search);
    const state = location.state as LoginLocationState | null;
    const returnTo =
      getSafeReturnPath(searchParams.get("returnTo")) ??
      getSafeReturnPath(state?.from ? getCurrentAppPath(state.from) : undefined);

    void navigate(
      returnTo ??
        (user.role === UserRole.ASSOCIATE
          ? paths.storeInsights.fittingRoomsLiveView
          : paths.storeInsights.performance),
      { replace: true },
    );
  }, [location.search, location.state, navigate, user]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isLoading) return;
    if (showSso && selectedProvider) {
      await handleSso(selectedProvider.id);
      return;
    }
    setError("");
    setIsLoading(true);

    try {
      if (providers === null) {
        setProviders(await CoreService.getInstance().discoverSsoProviders(emailOrUsername));
      } else {
        await login({ username: emailOrUsername, password });
      }
    } catch (err) {
      if (err instanceof LoginError && err.statusCode === 401) {
        setError(t("login.errors.invalidCredentials"));
      } else if (err instanceof LoginError && typeof err.statusCode === "number") {
        setError(t("login.errors.somethingWentWrongWithCode", { statusCode: err.statusCode }));
      } else {
        setError(t("login.errors.somethingWentWrong"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSso = async (providerId: number) => {
    setError("");
    setIsLoading(true);
    setIsSsoPending(true);
    setIsSsoLoadingVisible(false);
    setSsoRedirectUrl(null);
    try {
      const url = await CoreService.getInstance().startSso(emailOrUsername, providerId);
      setSsoRedirectUrl(url);
    } catch (err) {
      // A 400 carries a specific reason (such as a callback the API does not allow); anything else stays generic.
      setError(
        err instanceof LoginError && err.statusCode === 400
          ? err.message
          : t("login.errors.ssoStartFailed"),
      );
      setIsLoading(false);
      setIsSsoPending(false);
    }
  };

  useEffect(() => {
    if (isSsoPending && isSsoLoadingVisible && ssoRedirectUrl) {
      window.location.assign(ssoRedirectUrl);
    }
  }, [isSsoPending, isSsoLoadingVisible, ssoRedirectUrl]);

  return (
    <AuthPageLayout>
      <SignInPanel loading={isSsoPending} onLoadingEntered={() => setIsSsoLoadingVisible(true)}>
        <form onSubmit={(event) => void handleSubmit(event)}>
          <div className="space-y-5">
            <div>
              <label
                htmlFor="emailOrUsername"
                className="block text-md mb-2 text-left rtl:text-right"
              >
                {t("login.emailOrUsername")}
              </label>
              <AuthInput
                id="emailOrUsername"
                name="emailOrUsername"
                type="text"
                required
                placeholder={t("login.placeholders.emailOrUsername")}
                value={emailOrUsername}
                onChange={(event) => setEmailOrUsername(event.target.value)}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                disabled={isLoading || providers !== null}
              />
              <AuthTransition in={providers !== null}>
                <div className="flex justify-end pt-2" inert={providers === null}>
                  <button
                    type="button"
                    disabled={isLoading || providers === null}
                    className={authLinkClassName}
                    onClick={() => {
                      setProviders(null);
                      setSelectedProviderId(null);
                      setPassword("");
                      setUsePassword(false);
                      setError("");
                    }}
                  >
                    {t("login.actions.useDifferentAccount")}
                  </button>
                </div>
              </AuthTransition>
            </div>
          </div>

          <AuthTransition in={showPasswordForm}>
            <div className="pt-3">
              <PasswordLoginBody
                isActive={showPasswordForm}
                password={password}
                isLoading={isLoading}
                onPasswordChange={setPassword}
              />
            </div>
          </AuthTransition>
          <AuthTransition in={showSso && (providers?.length ?? 0) > 1}>
            <div className="pt-5" inert={!showSso}>
              {providers && selectedProvider && (
                <SsoLoginBody
                  providers={providers}
                  selectedProviderId={selectedProvider.id}
                  isLoading={isLoading || !showSso}
                  onProviderChange={setSelectedProviderId}
                />
              )}
            </div>
          </AuthTransition>

          <AuthFormError message={errorState.message} visible={Boolean(error)} />

          <AuthSubmitButton disabled={isLoading}>
            <Fade in={showSso} timeout={transitionDuration} mountOnEnter unmountOnExit>
              <span className="absolute inset-y-0 start-4 flex items-center" aria-hidden="true">
                <KeyOutlined />
              </span>
            </Fade>
            <span className="grid w-full place-items-center">
              {[
                { key: "loading", visible: isLoading, label: t("login.loading") },
                {
                  key: "continue",
                  visible: !isLoading && providers === null,
                  label: t("login.actions.continue"),
                },
                {
                  key: "sso",
                  visible: !isLoading && showSso,
                  label: t("login.actions.continueWithProvider", {
                    provider: selectedProvider?.name ?? "",
                  }),
                },
                {
                  key: "password",
                  visible: !isLoading && showPasswordForm,
                  label: t("login.submit"),
                },
              ].map(({ key, visible, label }) => (
                <Fade key={key} in={visible} timeout={transitionDuration}>
                  <span className="col-start-1 row-start-1" aria-hidden={!visible}>
                    {label}
                  </span>
                </Fade>
              ))}
            </span>
            <Fade in={showSso} timeout={transitionDuration} mountOnEnter unmountOnExit>
              <span className="absolute inset-y-0 end-4 flex items-center" aria-hidden="true">
                <ArrowForward className="rtl:rotate-180" />
              </span>
            </Fade>
          </AuthSubmitButton>
          <AuthTransition in={hasProviders}>
            <div className="text-center pt-4" inert={!hasProviders}>
              <Button
                type="button"
                disabled={isLoading || !hasProviders}
                onClick={() => {
                  setUsePassword((previous) => !previous);
                  setPassword("");
                  setError("");
                }}
              >
                <span className="grid place-items-center">
                  {[
                    { key: "sso", visible: usePassword, label: t("login.actions.useSso") },
                    {
                      key: "password",
                      visible: !usePassword,
                      label: t("login.actions.usePassword"),
                    },
                  ].map(({ key, visible, label }) => (
                    <Fade key={key} in={visible} timeout={transitionDuration}>
                      <span className="col-start-1 row-start-1" aria-hidden={!visible}>
                        {label}
                      </span>
                    </Fade>
                  ))}
                </span>
              </Button>
            </div>
          </AuthTransition>
        </form>
      </SignInPanel>
    </AuthPageLayout>
  );
};

export default Login;
